import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { listings, itemPath } from 'src/data/listings';
import Error404 from 'src/pages/404';

const isValidUrl = (url) => /^[a-z][a-z0-9+.-]*:/.test(url);

const ListingCard = ({ category, item, queryString }) => {
    const [imgError, setImgError] = useState(false);
    const className = "block bg-white relative before:content-[''] after:content-[''] before:absolute after:absolute before:top-0 before:left-0 after:bottom-0 after:right-0 before:w-0 after:w-0 before:h-0 after:h-0 before:border-t before:border-l before:border-black after:border-b after:border-r after:border-black before:transition-all after:transition-all before:duration-700 after:duration-700 hover:before:w-full hover:before:h-full hover:after:w-full hover:after:h-full";
    const content = (
        <>
            <div className="overflow-hidden">
                {item.thumbnail && !imgError ? (
                    <img src={item.thumbnail} alt={item.title} loading="lazy" onError={() => setImgError(true)} className="w-full h-64 object-cover object-top" />
                ) : (
                    <div className="w-full h-64 bg-[#F5F5F5] flex items-center justify-center text-2xl font-bold text-[#b81618]">JLR</div>
                )}
            </div>
            <div className="p-2">
                <h4 className="text-lg text-left text-black">{item.title}</h4>
            </div>
        </>
    );

    const query = queryString ? `?${queryString}` : '';

    if (isValidUrl(item.slug)) {
        return (
            <a className={className} href={item.slug} target="_blank" rel="noopener noreferrer">
                {content}
            </a>
        );
    }

    return (
        <Link className={className} to={`${itemPath(category, item)}${query}`}>
            {content}
        </Link>
    );
};

function ListingPage() {
    const navigate = useNavigate();
    const { category } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeCategory = searchParams.get('category') || 'Show Everything';
    const listing = listings[category];

    if (!listing) return <Error404 />;
    if (listing.skipListing && listing.items?.[0]) {
        return <Navigate to={itemPath(category, listing.items[0])} replace />;
    }

    const filteredItems = (activeCategory === 'Show Everything'
        ? listing.items
        : listing.items.filter((item) => {
            const itemCategories = Array.isArray(item.categories)
                ? item.categories
                : item.categories
                    ? [item.categories]
                    : Array.isArray(item.category)
                        ? item.category
                        : [item.category];
            return itemCategories.includes(activeCategory);
        })).sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date) - new Date(a.date);
    });

    return (
        <div className="site-page max-w-[1920px] w-full mx-auto">
            <section className="relative bg-white pt-[5rem] md:pb-8 pb-4 min-h-[calc(100vh-72px)]">
                <div className="max-w-[1280px] w-full mx-auto px-5 flex flex-col items-start">
                    <button onClick={() => navigate('/')} className='flex items-center mt-10 w-fit'>
                        <svg width="10" height="26" viewBox="0 0 15 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M14.2678 0.761522C15.2441 1.77689 15.2441 3.42311 14.2678 4.43848L6.03553 13L14.2678 21.5615C15.2441 22.5769 15.2441 24.2231 14.2678 25.2385C13.2915 26.2538 11.7085 26.2538 10.7322 25.2385L0.732233 14.8385C-0.244078 13.8231 -0.244078 12.1769 0.732233 11.1615L10.7322 0.761522C11.7085 -0.253841 13.2915 -0.253841 14.2678 0.761522Z" fill="black" />
                        </svg>
                        <div className="sm:text-xl text-lg font-bold ml-3">BACK</div>
                    </button>
                    <h3 className="sm:text-3xl text-xl text-[#b81618] font-bold sm:mb-5 mb-2 xs:mt-0 mt-5 w-full text-center">
                        {listing.title}
                    </h3>
                    {listing.categories && listing.categories.length > 0 && (
                        <div className="w-full flex flex-wrap justify-center gap-2 mb-8">
                            {[...listing.categories, 'Show Everything'].map((cat) => (
                                <button
                                    key={cat}
                                    type="button"
                                    onClick={() => setSearchParams(cat === 'Show Everything' ? {} : { category: cat })}
                                    className={`px-4 py-2 border border-black text-sm rounded-2xl transition duration-200 ${activeCategory === cat ? 'bg-black text-white' : 'bg-white text-black'}`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    )}
                    {filteredItems.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
                            {filteredItems.map((item) => (
                                <ListingCard key={item.id} category={category} item={item} queryString={searchParams.toString()} />
                            ))}
                        </div>
                    ) : (
                        <p className="text-[#9C1212] text-lg">No items yet.</p>
                    )}
                </div>
            </section>
        </div>
    );
}

export default ListingPage;
